import React, { useState, useEffect } from "react";
import { makeStyles, TextField, Grid, Paper, Typography, Divider } from "@material-ui/core";
import { Formik, Form, FastField, FieldArray } from "formik";
import { isArray } from "lodash";
import NumberFormat from "react-number-format";
import ButtonWithSpinner from "../ButtonWithSpinner";
import { i18n } from "../../translate/i18n";


const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
  },
  fullWidth: {
    width: "100%",
  },
  textfield: {
    width: "100%",
    fontSize: "0.875em"
  },
  control: {
    paddingRight: theme.spacing(0.5),
    paddingLeft: theme.spacing(0.5),
  },
  buttonContainer: {
    textAlign: "right",
    padding: theme.spacing(1),
  },
  dayCard: {
    borderRadius: 12,
    border: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(2),
    background: theme.palette.type === "dark" ? "rgba(255,255,255,0.02)" : theme.palette.background.paper,
    height: "100%",
  },
  dayTitle: {
    fontWeight: 700,
    marginBottom: theme.spacing(1),
  },
  sectionLabel: {
    fontSize: 12,
    fontWeight: 600,
    opacity: 0.85,
    marginBottom: theme.spacing(0.5),
  },
  introText: {
    marginBottom: theme.spacing(2),
    opacity: 0.85,
  },
  sectionDivider: {
    margin: `${theme.spacing(1.5)}px 0 ${theme.spacing(1.5)}px`,
  },
}));

function SchedulesForm(props) {
  const { initialValues, onSubmit, loading, labelSaveButton, embedded = false } = props;
  const classes = useStyles();

  const [schedules, setSchedules] = useState([
    { weekday: i18n.t("queueModal.serviceHours.monday"), weekdayEn: "monday", startTimeA: "", endTimeA: "", startTimeB: "", endTimeB: "", startDate: "", endDate: "" },
    { weekday: i18n.t("queueModal.serviceHours.tuesday"), weekdayEn: "tuesday", startTimeA: "", endTimeA: "", startTimeB: "", endTimeB: "", startDate: "", endDate: "" },
    { weekday: i18n.t("queueModal.serviceHours.wednesday"), weekdayEn: "wednesday", startTimeA: "", endTimeA: "", startTimeB: "", endTimeB: "", startDate: "", endDate: "" },
    { weekday: i18n.t("queueModal.serviceHours.thursday"), weekdayEn: "thursday", startTimeA: "", endTimeA: "", startTimeB: "", endTimeB: "", startDate: "", endDate: "" },
    { weekday: i18n.t("queueModal.serviceHours.friday"), weekdayEn: "friday", startTimeA: "", endTimeA: "", startTimeB: "", endTimeB: "", startDate: "", endDate: "" },
    { weekday: i18n.t("queueModal.serviceHours.saturday"), weekdayEn: "saturday", startTimeA: "", endTimeA: "", startTimeB: "", endTimeB: "", startDate: "", endDate: "" },
    { weekday: i18n.t("queueModal.serviceHours.sunday"), weekdayEn: "sunday", startTimeA: "", endTimeA: "", startTimeB: "", endTimeB: "", startDate: "", endDate: "" },
  ]);

  useEffect(() => {
    if (isArray(initialValues) && initialValues.length > 0) {
      setSchedules(initialValues.map(item => ({
        startDate: "",
        endDate: "",
        ...item
      })));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues]);

  const handleSubmit = (data) => {
    onSubmit(data);
  };

  const ContainerTag = embedded ? "div" : Form;

  return (
    <Formik
      enableReinitialize
      className={classes.fullWidth}
      initialValues={{ schedules }}
      onSubmit={({ schedules }) =>
        setTimeout(() => {
          handleSubmit(schedules);
        }, 500)
      }
    >
      {({ values }) => (
        <ContainerTag className={classes.fullWidth}>
          <Typography variant="body2" className={classes.introText}>
            Defina las franjas disponibles por día. Cada día puede tener hasta dos turnos y un rango opcional de fechas para activar esa disponibilidad.
          </Typography>
          <FieldArray
            name="schedules"
            render={(arrayHelpers) => (
              <Grid spacing={4} container>
                {values.schedules.map((item, index) => {
                  return (
                    <Grid key={index} xs={12} md={6} xl={4} item>
                      <Paper elevation={0} className={classes.dayCard}>
                        <Typography variant="h6" className={classes.dayTitle}>
                          {item.weekday}
                        </Typography>

                        <Typography className={classes.sectionLabel}>
                          Vigencia opcional
                        </Typography>
                        <Grid container>
                          <Grid className={classes.control} xs={12} sm={6} item>
                            <FastField
                              as={TextField}
                              label="Disponible desde"
                              name={`schedules[${index}].startDate`}
                              type="date"
                              variant="outlined"
                              className={classes.fullWidth}
                              margin="dense"
                              size="small"
                              InputLabelProps={{ shrink: true }}
                            />
                          </Grid>
                          <Grid className={classes.control} xs={12} sm={6} item>
                            <FastField
                              as={TextField}
                              label="Disponible hasta"
                              name={`schedules[${index}].endDate`}
                              type="date"
                              variant="outlined"
                              className={classes.fullWidth}
                              margin="dense"
                              size="small"
                              InputLabelProps={{ shrink: true }}
                            />
                          </Grid>
                        </Grid>

                        <Divider className={classes.sectionDivider} />

                        <Typography className={classes.sectionLabel}>
                          Turno A
                        </Typography>
                        <Grid container>
                          <Grid className={classes.control} xs={12} sm={6} item>
                            <FastField
                              label={i18n.t("queueModal.serviceHours.startTimeA")}
                              name={`schedules[${index}].startTimeA`}
                            >
                              {({ field }) => (
                                <NumberFormat
                                  {...field}
                                  variant="outlined"
                                  margin="dense"
                                  size="small"
                                  customInput={TextField}
                                  format="##:##"
                                  className={classes.fullWidth}
                                  label={i18n.t("queueModal.serviceHours.startTimeA")}
                                />
                              )}
                            </FastField>
                          </Grid>
                          <Grid className={classes.control} xs={12} sm={6} item>
                            <FastField
                              label={i18n.t("queueModal.serviceHours.endTimeA")}
                              name={`schedules[${index}].endTimeA`}
                            >
                              {({ field }) => (
                                <NumberFormat
                                  {...field}
                                  variant="outlined"
                                  margin="dense"
                                  size="small"
                                  customInput={TextField}
                                  format="##:##"
                                  className={classes.fullWidth}
                                  label={i18n.t("queueModal.serviceHours.endTimeA")}
                                />
                              )}
                            </FastField>
                          </Grid>
                        </Grid>

                        <Divider className={classes.sectionDivider} />

                        <Typography className={classes.sectionLabel}>
                          Turno B
                        </Typography>
                        <Grid container>
                          <Grid className={classes.control} xs={12} sm={6} item>
                            <FastField
                              label={i18n.t("queueModal.serviceHours.startTimeB")}
                              name={`schedules[${index}].startTimeB`}
                            >
                              {({ field }) => (
                                <NumberFormat
                                  {...field}
                                  variant="outlined"
                                  margin="dense"
                                  size="small"
                                  customInput={TextField}
                                  format="##:##"
                                  className={classes.fullWidth}
                                  label={i18n.t("queueModal.serviceHours.startTimeB")}
                                />
                              )}
                            </FastField>
                          </Grid>
                          <Grid className={classes.control} xs={12} sm={6} item>
                            <FastField
                              label={i18n.t("queueModal.serviceHours.endTimeB")}
                              name={`schedules[${index}].endTimeB`}
                            >
                              {({ field }) => (
                                <NumberFormat
                                  {...field}
                                  variant="outlined"
                                  margin="dense"
                                  size="small"
                                  customInput={TextField}
                                  format="##:##"
                                  className={classes.fullWidth}
                                  label={i18n.t("queueModal.serviceHours.endTimeB")}
                                />
                              )}
                            </FastField>
                          </Grid>
                        </Grid>
                      </Paper>
                    </Grid>
                  );
                })}
              </Grid>
            )}
          ></FieldArray>
          <div className={classes.buttonContainer}>
            <ButtonWithSpinner
              loading={loading}
              type={embedded ? "button" : "submit"}
              onClick={embedded ? () => handleSubmit(values.schedules) : undefined}
              color="primary"
              variant="contained"
            >
              {labelSaveButton ?? i18n.t("whatsappModal.buttons.okEdit")}
            </ButtonWithSpinner>
          </div>
        </ContainerTag>
      )}
    </Formik>
  );
}

export default SchedulesForm;
